import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DeliveryLogRow, NoteType, NotificationSchedule, User } from '@shared/types'
import { del, get, patch, post, put } from '../../lib/api'
import { DAY_NAMES, daysMaskLabel, formatTime, toggleDay } from '../../lib/format'
import { useSession } from '../../state/session'
import { useToast } from '../../state/toast'
import { Button, EmptyState, ErrorNote, Field, NumberInput, SectionHeading, Sheet, Spinner, TableScroll } from '../../components/ui'

type View = 'schedules' | 'matrix' | 'log'

interface SchedulesResponse {
  schedules: NotificationSchedule[]
  overrides: Record<string, Record<string, string>>
}

const BLANK: Partial<NotificationSchedule> = {
  key: '',
  label: '',
  localTime: '09:00',
  daysMask: 62,
  action: 'progress_note',
  noteType: 'status_update',
  title: '',
  body: '',
  requireClockedIn: true,
  active: true,
  sortOrder: 100,
  offerClockOut: false,
  returnAfterMin: null,
}

export default function AdminSchedules() {
  const toast = useToast()
  const [view, setView] = useState<View>('schedules')
  const [data, setData] = useState<SchedulesResponse | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([])
  const [editing, setEditing] = useState<Partial<NotificationSchedule> | null>(null)

  const load = useCallback(async () => {
    const [schedulesRes, usersRes, typesRes] = await Promise.all([
      get<SchedulesResponse>('/api/admin/schedules'),
      get<{ users: User[] }>('/api/admin/users'),
      get<{ noteTypes: NoteType[] }>('/api/admin/note-types'),
    ])
    setData(schedulesRes)
    setUsers(usersRes.users.filter((u) => u.active))
    setNoteTypes(typesRes.noteTypes)
  }, [])

  useEffect(() => {
    void load().catch((err) => toast.error(err instanceof Error ? err.message : 'Could not load'))
  }, [load, toast])

  if (!data) return <div className="py-16"><Spinner /></div>

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {(['schedules', 'matrix', 'log'] as View[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setView(value)}
            className={`btn flex-1 px-2 text-xs ${view === value ? 'btn-primary' : 'btn-secondary'}`}
          >
            {value === 'schedules' ? 'Reminders' : value === 'matrix' ? 'Who gets it' : 'Sent today'}
          </button>
        ))}
      </div>

      {view === 'schedules' && (
        <>
          <SectionHeading
            action={
              <Button className="min-h-9 px-3 text-xs" onClick={() => setEditing({ ...BLANK })}>
                Add reminder
              </Button>
            }
          >
            {data.schedules.length} reminders
          </SectionHeading>

          <div className="space-y-2">
            {data.schedules.map((schedule) => (
              <button
                key={schedule.id}
                type="button"
                onClick={() => setEditing(schedule)}
                className={`card block w-full p-3 text-left ${schedule.active ? '' : 'opacity-55'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold">{schedule.label}</p>
                    <p className="text-xs text-graphite-2">
                      {daysMaskLabel(schedule.daysMask)} ·{' '}
                      {schedule.action === 'clock_in' ? 'Clock in'
                        : schedule.action === 'clock_out' ? 'Clock out'
                          : 'Progress update'}
                      {schedule.requireClockedIn && ' · only when on the clock'}
                      {schedule.offerClockOut && ' · offers lunch clock-out'}
                    </p>
                    <p className="mt-1 text-xs text-graphite-2">
                      {schedule.assigneeIds?.length
                        ? `${schedule.assigneeIds.length} of ${users.length} crew`
                        : 'Nobody assigned'}
                    </p>
                  </div>
                  <span className="shrink-0 font-mono text-2xl font-semibold tnum">
                    {schedule.localTime}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {view === 'matrix' && (
        <AssignmentMatrix
          schedules={data.schedules}
          users={users}
          onSaved={load}
        />
      )}

      {view === 'log' && <DeliveryLog />}

      {editing && (
        <ScheduleSheet
          schedule={editing}
          users={users}
          noteTypes={noteTypes}
          overrides={editing.id ? data.overrides[editing.id] ?? {} : {}}
          onClose={() => setEditing(null)}
          onSaved={async (message) => {
            setEditing(null)
            toast.success(message)
            await load()
          }}
        />
      )}
    </div>
  )
}

/**
 * The crew × reminder grid.
 *
 * This is the answer to "who gets what notification at what times": one glance
 * shows the whole company's coverage, and a tap writes it. Each column saves
 * independently so an accidental double-tap cannot wipe the grid.
 */
function AssignmentMatrix({
  schedules, users, onSaved,
}: {
  schedules: NotificationSchedule[]
  users: User[]
  onSaved: () => Promise<void>
}) {
  const toast = useToast()
  const [assignments, setAssignments] = useState<Record<string, Set<string>>>(() =>
    Object.fromEntries(schedules.map((s) => [s.id, new Set(s.assigneeIds ?? [])])))
  const [saving, setSaving] = useState<string | null>(null)

  const active = useMemo(() => schedules.filter((s) => s.active), [schedules])

  const persist = async (scheduleId: string, userIds: Set<string>) => {
    setSaving(scheduleId)
    try {
      await put(`/api/admin/schedules/${scheduleId}/assignees`, { userIds: [...userIds] })
      await onSaved()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save')
    } finally {
      setSaving(null)
    }
  }

  const toggle = (scheduleId: string, userId: string) => {
    setAssignments((current) => {
      const next = new Set(current[scheduleId] ?? [])
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      void persist(scheduleId, next)
      return { ...current, [scheduleId]: next }
    })
  }

  const toggleColumn = (scheduleId: string) => {
    setAssignments((current) => {
      const currentSet = current[scheduleId] ?? new Set()
      const next = currentSet.size === users.length ? new Set<string>() : new Set(users.map((u) => u.id))
      void persist(scheduleId, next)
      return { ...current, [scheduleId]: next }
    })
  }

  if (active.length === 0 || users.length === 0) {
    return (
      <EmptyState
        title="Nothing to assign yet"
        body="Add at least one reminder and one active crew member."
      />
    )
  }

  return (
    <div>
      <p className="mb-3 text-sm text-graphite-2">
        Tap a cell to turn a reminder on or off for that person. Changes save immediately.
      </p>
      <TableScroll>
        <table className="grid-table min-w-max">
          <thead>
            <tr>
              <th className="sticky left-0 bg-concrete">Crew</th>
              {active.map((schedule) => (
                <th key={schedule.id} className="text-center">
                  <button
                    type="button"
                    onClick={() => toggleColumn(schedule.id)}
                    className="block w-full leading-tight"
                    title="Turn on or off for everyone"
                  >
                    <span className="block font-mono text-xs text-graphite">
                      {schedule.localTime}
                    </span>
                    <span className="block max-w-20 truncate">{schedule.label}</span>
                    {saving === schedule.id && <span className="block text-graphite">saving…</span>}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td className="sticky left-0 bg-concrete font-semibold whitespace-nowrap">
                  {user.name}
                </td>
                {active.map((schedule) => {
                  const on = assignments[schedule.id]?.has(user.id) ?? false
                  return (
                    <td key={schedule.id} className="text-center">
                      <button
                        type="button"
                        onClick={() => toggle(schedule.id, user.id)}
                        aria-pressed={on}
                        aria-label={`${schedule.label} for ${user.name}`}
                        className={`size-8 border ${
                          on
                            ? 'border-brand bg-brand text-ink'
                            : 'border-line-strong bg-transparent text-transparent'
                        }`}
                      >
                        ✓
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </div>
  )
}

function ScheduleSheet({
  schedule, users, noteTypes, overrides, onClose, onSaved,
}: {
  schedule: Partial<NotificationSchedule>
  users: User[]
  noteTypes: NoteType[]
  overrides: Record<string, string>
  onClose: () => void
  onSaved: (message: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<Partial<NotificationSchedule>>(schedule)
  const [assignees, setAssignees] = useState<Set<string>>(new Set(schedule.assigneeIds ?? []))
  const [overrideTimes, setOverrideTimes] = useState<Record<string, string>>(overrides)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const isNew = !schedule.id

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const payload = {
        key: draft.key,
        label: draft.label,
        localTime: draft.localTime,
        daysMask: draft.daysMask,
        action: draft.action,
        noteType: draft.action === 'clock_in' ? null : draft.noteType,
        title: draft.title || draft.label,
        body: draft.body || 'Time for an update.',
        requireClockedIn: draft.requireClockedIn,
        active: draft.active,
        sortOrder: draft.sortOrder,
        offerClockOut: draft.action === 'progress_note' ? draft.offerClockOut ?? false : false,
        returnAfterMin: draft.action === 'progress_note' && draft.offerClockOut
          ? draft.returnAfterMin ?? null
          : null,
      }

      const id = isNew
        ? (await post<{ schedule: NotificationSchedule }>('/api/admin/schedules', payload)).schedule.id
        : (await patch<{ schedule: NotificationSchedule }>(`/api/admin/schedules/${schedule.id}`, payload)).schedule.id

      await put(`/api/admin/schedules/${id}/assignees`, {
        userIds: [...assignees],
        overrides: Object.fromEntries(
          [...assignees].map((userId) => [userId, overrideTimes[userId] || null]),
        ),
      })

      await onSaved(isNew ? 'Reminder added' : 'Reminder updated')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the reminder')
      setBusy(false)
    }
  }

  const removeSchedule = async () => {
    if (!schedule.id) return
    setBusy(true)
    try {
      await del(`/api/admin/schedules/${schedule.id}`)
      await onSaved('Reminder deleted')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete')
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={isNew ? 'New reminder' : draft.label ?? 'Reminder'}
      footer={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" onClick={() => void removeSchedule()} disabled={busy}>
              Delete
            </Button>
          )}
          <Button variant="primary" className="flex-1" loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required>
            <input
              className="field"
              value={draft.label ?? ''}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Morning break update"
            />
          </Field>
          <Field label="Time" required>
            <input
              className="field font-mono"
              type="time"
              value={draft.localTime ?? '09:00'}
              onChange={(e) => setDraft({ ...draft, localTime: e.target.value })}
            />
          </Field>
        </div>

        {isNew && (
          <Field label="Key" hint="Short id used in reports. Letters, numbers, underscores." required>
            <input
              className="field font-mono"
              value={draft.key ?? ''}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              placeholder="break_am"
            />
          </Field>
        )}

        <Field label="Days">
          <div className="flex gap-1">
            {DAY_NAMES.map((name, index) => {
              const on = ((draft.daysMask ?? 62) & (1 << index)) !== 0
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setDraft({ ...draft, daysMask: toggleDay(draft.daysMask ?? 62, index) })}
                  aria-pressed={on}
                  className={`btn flex-1 px-0 text-xs ${on ? 'btn-primary' : 'btn-secondary'}`}
                >
                  {name.slice(0, 1)}
                </button>
              )
            })}
          </div>
        </Field>

        <Field label="What it asks for">
          <select
            className="field"
            value={draft.action ?? 'progress_note'}
            onChange={(e) => setDraft({ ...draft, action: e.target.value as NotificationSchedule['action'] })}
          >
            <option value="clock_in">Clock in to a site</option>
            <option value="progress_note">Post a progress update</option>
            <option value="clock_out">Clock out for the day</option>
          </select>
        </Field>

        {draft.action !== 'clock_in' && (
          <Field label="Update is tagged as">
            <select
              className="field"
              value={draft.noteType ?? 'status_update'}
              onChange={(e) => setDraft({ ...draft, noteType: e.target.value })}
            >
              {noteTypes.map((type) => (
                <option key={type.key} value={type.key}>{type.label}</option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Notification title">
          <input
            className="field"
            value={draft.title ?? ''}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Morning break — progress photo"
          />
        </Field>

        <Field label="Notification message">
          <input
            className="field"
            value={draft.body ?? ''}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            placeholder="Take a photo of where the work stands."
          />
        </Field>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="size-5 accent-[var(--wm-brand-deep)]"
            checked={draft.requireClockedIn ?? false}
            onChange={(e) => setDraft({ ...draft, requireClockedIn: e.target.checked })}
          />
          <span className="text-sm">Only send when the person is clocked in</span>
        </label>

        {draft.action === 'progress_note' && (
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 size-5 accent-[var(--wm-brand-deep)]"
              checked={draft.offerClockOut ?? false}
              onChange={(e) => setDraft({
                ...draft,
                offerClockOut: e.target.checked,
                returnAfterMin: e.target.checked ? draft.returnAfterMin ?? 60 : null,
              })}
            />
            <span className="text-sm">
              Offer “clock out for lunch”
              <span className="block text-xs text-graphite-2">
                Optional for the crew — they can post the update and stay on the clock.
              </span>
            </span>
          </label>
        )}

        {draft.action === 'progress_note' && draft.offerClockOut && (
          <Field
            label="Remind them to clock back in after (minutes)"
            hint="Counted from when they clock out. Clear it for no reminder."
          >
            <NumberInput
              className="font-mono"
              min={5}
              max={240}
              value={draft.returnAfterMin ?? undefined}
              onChange={(n) => setDraft({ ...draft, returnAfterMin: n })}
            />
          </Field>
        )}

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="size-5 accent-[var(--wm-brand-deep)]"
            checked={draft.active ?? true}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
          />
          <span className="text-sm">Active</span>
        </label>

        <div>
          <SectionHeading
            action={
              <button
                type="button"
                className="link text-xs"
                onClick={() =>
                  setAssignees(
                    assignees.size === users.length ? new Set() : new Set(users.map((u) => u.id)),
                  )}
              >
                {assignees.size === users.length ? 'Clear all' : 'Select everyone'}
              </button>
            }
          >
            Who gets it ({assignees.size})
          </SectionHeading>

          <div className="space-y-1">
            {users.map((user) => {
              const on = assignees.has(user.id)
              return (
                <div key={user.id} className="flex items-center gap-2 border-b border-line py-1.5">
                  <label className="flex min-w-0 flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      className="size-5 shrink-0 accent-[var(--wm-brand-deep)]"
                      checked={on}
                      onChange={() => {
                        const next = new Set(assignees)
                        if (on) next.delete(user.id)
                        else next.add(user.id)
                        setAssignees(next)
                      }}
                    />
                    <span className="truncate text-sm">{user.name}</span>
                  </label>
                  {on && (
                    <input
                      type="time"
                      // No text-size class here: anything under 16px makes iOS
                      // zoom the page on focus and stay zoomed.
                      className="field w-32 min-h-10 py-1 font-mono"
                      value={overrideTimes[user.id] ?? ''}
                      onChange={(e) =>
                        setOverrideTimes({ ...overrideTimes, [user.id]: e.target.value })}
                      title={`Override time for ${user.name}`}
                      placeholder={draft.localTime}
                    />
                  )}
                </div>
              )
            })}
          </div>
          <p className="mt-2 text-xs text-graphite-2">
            Set a time next to someone to override the reminder for them — useful for an early crew.
          </p>
        </div>

        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  )
}

function DeliveryLog() {
  const { settings } = useSession()
  const [rows, setRows] = useState<DeliveryLogRow[] | null>(null)

  useEffect(() => {
    void get<{ deliveries: DeliveryLogRow[] }>('/api/admin/deliveries')
      .then((res) => setRows(res.deliveries))
      .catch(() => setRows([]))
  }, [])

  if (!rows) return <div className="py-16"><Spinner /></div>
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Nothing sent today"
        body="Reminders appear here as the schedule fires, with whether each one reached a device."
      />
    )
  }

  const tone: Record<string, string> = {
    sent: 'text-ok',
    failed: 'text-alarm',
    no_subscription: 'text-alarm',
    skipped: 'text-graphite-2',
  }

  return (
    <TableScroll>
      <table className="grid-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Crew</th>
            <th>Reminder</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="font-mono text-xs tnum">{formatTime(row.sentAt, settings.timezone)}</td>
              <td>{row.userName}</td>
              <td className="text-graphite-2">{row.scheduleLabel}</td>
              <td className={`font-mono text-xs ${tone[row.status] ?? ''}`}>
                {row.status === 'no_subscription' ? 'no device' : row.status}
                {row.detail && row.status !== 'sent' && (
                  <span className="block text-[10px] text-graphite-2">{row.detail}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  )
}
